import type {
  PublicError,
  PublicRequestId,
} from '@ballpeek/api/client'
import type { ZodIssue, ZodType } from 'zod'

import {
  publicErrorSchema,
  publicRequestIdSchema,
} from '@ballpeek/api/client'

export type PublicFootballErrorCode = PublicError['error']['issues'][number]['code']

export class PublicFootballRequestError extends Error {
  readonly code: PublicFootballErrorCode | null
  readonly publicError: PublicError | null
  readonly requestId: PublicRequestId | null
  readonly status: number

  constructor(input: {
    message: string
    publicError: PublicError | null
    requestId: PublicRequestId | null
    status: number
  }) {
    super(input.message)
    this.name = 'PublicFootballRequestError'
    this.code = input.publicError?.error.issues[0]?.code ?? null
    this.publicError = input.publicError
    this.requestId = input.requestId
    this.status = input.status
  }
}
export class PublicFootballRouteInputError extends Error {
  readonly code = 'validation_error' as const
  readonly issues: ZodIssue[]
  readonly status = 422 as const
  readonly statusCode = 422 as const

  constructor(issues: ZodIssue[]) {
    super('Public Football route input is malformed')
    this.name = 'PublicFootballRouteInputError'
    this.issues = issues
  }
}

export function parsePublicFootballRouteInput<T>(schema: ZodType<T>, input: unknown): T {
  const result = schema.safeParse(input)
  if (!result.success)
    throw new PublicFootballRouteInputError(result.error.issues)
  return result.data
}

export async function readPublicFootballResponse<T>(
  response: Response,
  schema: ZodType<T>,
  errorMessage: string,
): Promise<T> {
  let payload: unknown
  try {
    payload = await response.json()
  }
  catch (error) {
    if (response.ok)
      throw error
    payload = undefined
  }

  if (!response.ok) {
    const parsedError = publicErrorSchema.safeParse(payload)
    const parsedRequestId = publicRequestIdSchema.safeParse(response.headers.get('X-Request-Id'))
    throw new PublicFootballRequestError({
      message: errorMessage,
      publicError: parsedError.success ? parsedError.data : null,
      requestId: parsedRequestId.success ? parsedRequestId.data : null,
      status: response.status,
    })
  }

  return schema.parse(payload)
}
